/**
 * Glocke in der Kopfleiste: Hinweise an den angemeldeten Benutzer.
 *
 * Der Server schickt neue Hinweise ueber einen Ereignisstrom (Server-Sent
 * Events); der Browser verbindet sich bei Abbruch von selbst neu. Ein neuer
 * Hinweis erhoeht den Zaehler, erscheint kurz als Einblendung und - wenn der
 * Anwender es einmal erlaubt hat - als Systembenachrichtigung, die auch bei
 * einem Canalis-Tab im Hintergrund auffaellt. Systembenachrichtigungen setzen
 * HTTPS voraus (oder localhost); ohne geht der Rest trotzdem.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, apiUrl } from '../api/client';
import type { AppNotification } from '../api/types';
import { useToast } from './toast';

type Permission = NotificationPermission | 'unsupported';

function desktopPermission(): Permission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/** "vor 5 Min", "gestern", sonst Datum. */
function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} Min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} Std`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'gestern';
  if (days < 7) return `vor ${days} Tagen`;
  return new Date(iso).toLocaleDateString('de-DE');
}

export function NotificationBell() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [permission, setPermission] = useState<Permission>(desktopPermission);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const list = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ notifications: AppNotification[]; unread: number }>('/api/notifications'),
  });
  useEffect(() => {
    if (list.data) setUnread(list.data.unread);
  }, [list.data]);

  // Ereignisstrom: neue Hinweise sofort.
  useEffect(() => {
    const source = new EventSource(apiUrl('/api/notifications/stream'));
    source.addEventListener('hello', (event) => {
      const data = JSON.parse((event as MessageEvent).data) as { unread: number };
      setUnread(data.unread);
      // Nach einem Verbindungsabbruch koennte die Liste veraltet sein.
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    });
    source.addEventListener('notification', (event) => {
      const row = JSON.parse((event as MessageEvent).data) as AppNotification;
      queryClient.setQueryData<{ notifications: AppNotification[]; unread: number }>(['notifications'], (current) => ({
        notifications: [row, ...(current?.notifications ?? [])].slice(0, 50),
        unread: (current?.unread ?? 0) + 1,
      }));
      setUnread((current) => current + 1);
      toast.info(row.title);
      if (desktopPermission() === 'granted') {
        try {
          const desktop = new Notification(row.title, { body: row.body ?? undefined, tag: row.id });
          desktop.onclick = () => {
            window.focus();
            void openNotification(row);
          };
        } catch {
          /* Systembenachrichtigungen sind hier nicht moeglich (z. B. ohne HTTPS). */
        }
      }
    });
    return () => source.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Klick ausserhalb schliesst die Liste.
  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  async function markRead(ids: string[] | 'all') {
    const result = await api.post<{ unread: number }>('/api/notifications/read', ids === 'all' ? { all: true } : { ids });
    setUnread(result.unread);
    queryClient.setQueryData<{ notifications: AppNotification[]; unread: number }>(['notifications'], (current) =>
      current
        ? {
            unread: result.unread,
            notifications: current.notifications.map((n) =>
              ids === 'all' || ids.includes(n.id) ? { ...n, readAt: n.readAt ?? new Date().toISOString() } : n,
            ),
          }
        : current,
    );
  }

  async function openNotification(row: AppNotification) {
    setOpen(false);
    if (!row.readAt) await markRead([row.id]).catch(() => undefined);
    if (row.link) navigate(row.link);
  }

  async function requestDesktop() {
    if (permission === 'unsupported') return;
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted') toast.success('Systembenachrichtigungen sind aktiv.');
  }

  const rows = list.data?.notifications ?? [];

  return (
    <div className="bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className="btn btn-sm btn-quiet bell"
        title={unread > 0 ? `${unread} ungelesene Hinweise` : 'Hinweise'}
        aria-label="Hinweise"
        onClick={() => setOpen((current) => !current)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unread > 0 ? <span className="bell-badge">{unread > 99 ? '99+' : unread}</span> : null}
      </button>

      {open ? (
        <div className="notif-panel" role="dialog" aria-label="Hinweise">
          <div className="notif-head">
            <strong>Hinweise</strong>
            {unread > 0 ? (
              <button type="button" className="btn btn-sm btn-quiet" onClick={() => void markRead('all')}>
                Alle gelesen
              </button>
            ) : null}
          </div>
          <div className="notif-list">
            {rows.length === 0 ? <div className="faint" style={{ padding: 14 }}>Keine Hinweise.</div> : null}
            {rows.map((row) => (
              <button
                type="button"
                key={row.id}
                className={`notif-item${row.readAt ? '' : ' unread'}`}
                onClick={() => void openNotification(row)}
              >
                <div className="notif-title">{row.title}</div>
                {row.body ? <div className="notif-body">{row.body}</div> : null}
                <div className="notif-time">{relativeTime(row.createdAt)}</div>
              </button>
            ))}
          </div>
          <div className="notif-foot faint">
            {permission === 'granted' ? (
              'Systembenachrichtigungen aktiv.'
            ) : permission === 'denied' ? (
              'Systembenachrichtigungen im Browser blockiert (Seiteneinstellungen).'
            ) : permission === 'unsupported' ? (
              'Systembenachrichtigungen braucht HTTPS.'
            ) : (
              <button type="button" className="btn btn-sm" onClick={() => void requestDesktop()}>
                Systembenachrichtigungen erlauben
              </button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
